import { useState, useRef, useEffect } from 'react';
import { FaImage, FaFile, FaTimes, FaUpload } from 'react-icons/fa';
import { uploadFile, validateFile, formatFileSize } from '../../services/fileUpload';
import { useTranslation } from 'react-i18next';

export default function FileUpload({
  onUploadSuccess,
  onUploadError,
  accept = 'image/*',
  // maxSize = 5 * 1024 * 1024, // 5MB
  allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
  value = null,
  preview = true,
  showProgress = true,
  className = '',
  uploadEndpoint = '/upload',
  baseUrl = ''
}) {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(value);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  // Update preview when value prop changes
  useEffect(() => {
    if (value && value !== previewUrl) {
      setPreviewUrl(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
    // eslint-disable-next-line react-hooks/exhaustive-deps

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file
    const validation = validateFile(file, { allowedTypes });
    if (!validation.valid) {
      if (onUploadError) {
        onUploadError(new Error(validation.error));
      }
      return;
    }

    setSelectedFile(file);

    // Create preview for images
    if (preview && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }

    // Auto-upload
    await handleUpload(file);
  };

  const handleUpload = async (file) => {
    if (!file) return;

    setUploading(true);
    setProgress(0);

    try {
      const url = await uploadFile(
        file,
        (progressValue) => {
          setProgress(progressValue);
        },
        {
          // maxSize,
          allowedTypes,
          endpoint: uploadEndpoint,
        }
      );

      if (onUploadSuccess) {
        onUploadSuccess(url);
      }

      setUploading(false);
      setProgress(100);
    } catch (error) {
      console.error('Upload error:', error);
      setUploading(false);
      setProgress(0);
      setPreviewUrl(null);
      setSelectedFile(null);

      if (onUploadError) {
        onUploadError(error);
      }
    }
  };

  const handleRemove = () => {
    setPreviewUrl(null);
    setSelectedFile(null);
    setProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (onUploadSuccess) {
      onUploadSuccess(null);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const isImage = accept.includes('image') || allowedTypes.some(type => type.startsWith('image'));

  return (
    <div className={`relative ${className}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileSelect}
        className="hidden"
        disabled={uploading}
      />

      <div
        onClick={handleClick}
        className={`relative border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
          uploading ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-blue-400'
        } ${previewUrl ? 'border-solid' : ''}`}
      >
        {previewUrl ? (
          <div className="relative w-full h-full">
            {isImage ? (
              <img
                src={previewUrl.startsWith('http') || previewUrl.startsWith('data:')
                  ? previewUrl
                  : `${baseUrl}${previewUrl}`}
                alt="Preview"
                className="w-full h-full object-cover rounded-lg"
                onError={(e) => {
                  console.error('Image load error:', previewUrl);
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <div className="flex flex-col items-center justify-center p-4">
                <FaFile size={40} className="text-gray-400 mb-2" />
                <p className="text-sm text-gray-600">{selectedFile?.name}</p>
                <p className="text-xs text-gray-400">{formatFileSize(selectedFile?.size || 0)}</p>
              </div>
            )}

            {!uploading && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove();
                }}
                className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
              >
                <FaTimes size={12} />
              </button>
            )}

            {uploading && showProgress && (
              <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center">
                <div className="text-white text-center">
                  <div className="mb-2">
                    <FaUpload size={24} className="animate-bounce mx-auto" />
                  </div>
                  <p className="text-sm font-semibold">{progress}%</p>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center p-8 text-gray-400">
            {isImage ? (
              <>
                <FaImage size={40} className="mb-2" />
                <p className="text-sm">{t('Click to upload image')}</p>
              </>
            ) : (
              <>
                <FaFile size={40} className="mb-2" />
                <p className="text-sm">{t('Click to upload file')}</p>
              </>
            )}
            {/* <p className="text-xs mt-1">Max size: {formatFileSize(maxSize)}</p> */}
          </div>
        )}
      </div>

      {uploading && showProgress && !previewUrl && (
        <div className="mt-2">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1 text-center">{progress}%</p>
        </div>
      )}
    </div>
  );
}

